import * as React from 'react';
import { CircleSpinner } from 'ui/spinner';

import { BUTTON_SIZES, BUTTON_VARIANTS } from './constants';
import { BaseButton, StaticFauxButton } from './Styled';

/**
 * NOTE: React Docgen cannot render flow types unless they are defined in the
 * same file as the component referencing them.  For that reason, they have been
 * moved from components to here
 *
 * More info: https://github.com/reactjs/react-docgen#flow-and-typescript-support
 */

// prettier-ignore

export interface ButtonProps {
  autoFocus?: boolean; // is this where this should be added?
  /** for type="submit" buttons, the form associates the button
   *  to the form if it located outside of the form */
  form?: string;
  /** element id passed for use as a key as a child of FormField */
  id?: string;
  /** An override to disable the button */
  isDisabled?: boolean;
  /** An override to show loading state */
  isLoading?: boolean;
  /** The click handler for the button */
  onClick?: (e: React.MouseEvent) => void;
  /** The size variant of the button */
  size?: BUTTON_SIZES;
  /** The type variant of the button */
  variant?: BUTTON_VARIANTS;
  children?: React.ReactNode;
  tabIndex?: number;
  type?: 'button' | 'submit' | 'reset';
  role?: string;
  ref?: React.Ref<HTMLButtonElement> | null;
}

export const Button = React.forwardRef(
  (
    {
      isDisabled = false,
      isLoading = false,
      onClick = () => {
        // default parameter for onClick
      },
      size = BUTTON_SIZES.MEDIUM,
      variant = BUTTON_VARIANTS.PRIMARY,
      ...props
    }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const handleClick = (e: React.MouseEvent) => {
      // @ts-ignore
      ref?.current?.focus(); // force the focus for safari/firefox
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <BaseButton
        aria-disabled={isDisabled}
        disabled={isDisabled}
        $loading={isLoading}
        data-test={`${size}-${variant}-button${isDisabled ? '-disabled' : ''}${
          isLoading ? '-loading' : ''
        }`}
        onClick={handleClick}
        ref={ref}
        size={size}
        variant={variant}
        {...props}
      >
        {isLoading ? (
          <>
            <CircleSpinner size={16} isColorInherited />
            <span>{props.children}</span>
          </>
        ) : (
          props.children
        )}
      </BaseButton>
    );
  },
);

// looks like a button, but not clickable
export type FauxButtonProps = {
  /** element id passed for use as a key as a child of FormField */
  id?: string;
  /** The size variant of the button */
  size?: BUTTON_SIZES;
  /** The type variant of the button */
  variant?: BUTTON_VARIANTS;
  children?: React.ReactNode;
};

export const FauxButton = ({
  children,
  size = BUTTON_SIZES.MEDIUM,
  variant = BUTTON_VARIANTS.PRIMARY,
  ...props
}: FauxButtonProps) => {
  return (
    <StaticFauxButton size={size} variant={variant} {...props}>
      {children}
    </StaticFauxButton>
  );
};

export default Button;
